import { MONTHS } from './constants';

/**
 * Important dates use calendarDate as UTC midnight ISO (one calendar day in UTC).
 * Parsing with local getters shows the prior calendar day west of UTC; this maps UTC
 * Y-M-D to a local Date at midnight for that same wall calendar day everywhere.
 */
export function utcCalendarIsoToLocalMidnight(dateInput) {
  const parsed = new Date(dateInput);
  if (Number.isNaN(parsed.getTime())) return parsed;
  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate()
  );
}

export const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const isInRange = (date, start, end) =>
  date.getTime() >= start.getTime() && date.getTime() <= end.getTime();

export const calendarEventAppliesToDay = (day, ev) => {
  if (!ev.startTime) return false;
  const startDay = new Date(ev.startTime);
  startDay.setHours(0, 0, 0, 0);
  const endDay = ev.endTime ? new Date(ev.endTime) : new Date(ev.startTime);
  endDay.setHours(0, 0, 0, 0);
  const d = new Date(day);
  d.setHours(0, 0, 0, 0);
  return d.getTime() >= startDay.getTime() && d.getTime() <= endDay.getTime();
};

/** Whether `ev` spans a single calendar day (user event or company important date). */
function isSingleCalendarDayImportantDate(ev) {
  const start = utcCalendarIsoToLocalMidnight(ev.calendarDate);
  const end = new Date(ev.endDate ?? ev.calendarDate);
  return isSameDay(start, end);
}

function isSingleCalendarDayCalendarEvent(ev) {
  if (!ev.startTime) return false;
  const start = new Date(ev.startTime);
  const end = new Date(ev.endTime || ev.startTime);
  return isSameDay(start, end);
}

/** Any event that touches this calendar day (tooltips, aria, list). */
export function miniCalendarEventAppliesToDay(day, ev) {
  if (ev.type === 'calendarEvent') {
    return calendarEventAppliesToDay(day, ev);
  }
  const d = new Date(day);
  d.setHours(0, 0, 0, 0);
  const start = utcCalendarIsoToLocalMidnight(ev.calendarDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(ev.endDate ?? ev.calendarDate);
  end.setHours(0, 0, 0, 0);
  return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
}

/**
 * Full background shade for the date circle: single-day user/company events,
 * and start/end days of blackout ranges (middle days use dots only).
 */
export function miniCalendarEventShowsFullShade(day, ev) {
  if (!miniCalendarEventAppliesToDay(day, ev)) return false;
  if (ev.type === 'calendarEvent') {
    return isSingleCalendarDayCalendarEvent(ev);
  }
  if (ev.type === 'importantDate' && ev.isBlackoutEvent) {
    const start = utcCalendarIsoToLocalMidnight(ev.calendarDate);
    const end = new Date(ev.endDate ?? ev.calendarDate);
    return isSameDay(day, start) || isSameDay(day, end);
  }
  if (ev.type === 'importantDate') {
    return isSingleCalendarDayImportantDate(ev);
  }
  return false;
}

/** Dots on days in range except where that event uses full shade instead. */
export function miniCalendarShowsDotForEvent(day, ev) {
  return (
    miniCalendarEventAppliesToDay(day, ev) &&
    !miniCalendarEventShowsFullShade(day, ev)
  );
}

/** Full-shade events for a day, ordered for stable split backgrounds. */
export function miniCalendarFullShadeEventsForDay(day, events) {
  const shade = events.filter(ev => miniCalendarEventShowsFullShade(day, ev));
  return miniCalendarSortShadeEvents(shade);
}

export function miniCalendarSortShadeEvents(shadeEvents) {
  return [...shadeEvents].sort((a, b) => {
    const tA = (
      a.type === 'calendarEvent'
        ? new Date(a.startTime ?? a.calendarDate)
        : utcCalendarIsoToLocalMidnight(a.calendarDate)
    ).getTime();
    const tB = (
      b.type === 'calendarEvent'
        ? new Date(b.startTime ?? b.calendarDate)
        : utcCalendarIsoToLocalMidnight(b.calendarDate)
    ).getTime();
    if (tA !== tB) return tA - tB;
    return a.type === 'calendarEvent' ? -1 : 1;
  });
}

export const fmtShort = d =>
  `${MONTHS[d.getMonth()].slice(0, 3)} ${String(d.getDate()).padStart(
    2,
    '0'
  )} ${d.getFullYear()}`;

export function getMiniCalendarEventDisplayName(ev) {
  const title = typeof ev.title === 'string' ? ev.title.trim() : '';
  if (title) return title;
  const badge = typeof ev.badgeTitle === 'string' ? ev.badgeTitle.trim() : '';
  if (badge) return badge;
  return 'Event';
}

/** Single-line range for mini-calendar tooltips (uses calendarDate / endDate from normalized events). */
export function formatMiniCalendarEventDateRange(ev) {
  const start =
    ev.type === 'calendarEvent'
      ? new Date(ev.calendarDate)
      : utcCalendarIsoToLocalMidnight(ev.calendarDate);
  const end = new Date(ev.endDate ?? ev.calendarDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  const sameCalendarDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (sameCalendarDay) return fmtShort(start);
  return `${fmtShort(start)} – ${fmtShort(end)}`;
}
